/**
 * Чистая модель экрана карты: всё, что можно проверить без WebGL.
 *
 * maplibre в jsdom не поднимается, поэтому решения, которые легко сломать
 * рефакторингом — какой bbox уходит на сервер, каким цветом красится массив,
 * что вообще попадает в слой, — вынесены сюда и покрыты тестами. Компонент
 * карты остаётся тонким: он применяет посчитанное здесь, а не решает сам.
 *
 * Главный инвариант модуля: единственный вход геометрии на карту — это
 * `contourLayerData`. Факт без ПРИНЯТОЙ геометрии не превращается ни в
 * полигон, ни в центроид, ни в маркер, ни в какую другую отметку.
 */

import type { ColorSpecification, DataDrivenPropertyValueSpecification } from "maplibre-gl";
import type {
  ContourFeature,
  ContourFeatureProps,
  ReferenceFeature,
} from "../../api/client";
import {
  CROP_ORDER,
  cropLabel,
  cropRoleLabel,
  rotationPhaseLabel,
} from "../../lib/crops";
import { formatArea, formatCount, missingLabel } from "../../lib/format";
import { defineMessages } from "../../i18n/define";
import { tr } from "../../i18n/runtime";
import {
  MULTI_CONTOUR_RELATIONS,
  relationLabels,
  sharedGeometryNote,
  tierLabels,
  TIER_ORDER,
} from "./tierRegistry";

const m = defineMessages({
  unknown: {
    id: "datamap.model.unknown",
    defaultMessage: "неизвестно",
    description:
      "Значение в подсказке карты (ярус или отношение геометрии), когда сервер не прислал код; строчная, одно слово",
  },
  unknownCode: {
    id: "datamap.model.unknownCode",
    defaultMessage: "неизвестно: {code}",
    description:
      "Значение в подсказке карты: сервер прислал код яруса или отношения, которого интерфейс не знает; {code} — машинный код, показываем как есть",
  },
  haValue: {
    id: "datamap.model.haValue",
    defaultMessage: "{value} га",
    description:
      "Площадь с единицей в подсказке и превью контура на карте; {value} — число, уже отформатированное («46,5»)",
  },
  massifUnnamed: {
    id: "datamap.model.massifUnnamed",
    defaultMessage: "массив #{id}",
    description:
      "Подсказка и превью контура: имя массива из справочника ещё не загружено, показываем номер; {id} — номер массива",
  },
  landUserMany: {
    id: "datamap.model.landUserMany",
    defaultMessage: "несколько: {count}",
    description:
      "Подсказка и превью контура: у нарезанного контура несколько землепользователей, главного нет; {count} — их число",
  },
  truncation: {
    id: "datamap.model.truncation",
    defaultMessage:
      "Показано {shown} из {matchingText} {count, plural, one {контура} few {контуров} many {контуров} other {контура}} в этой области — увеличьте масштаб, чтобы увидеть остальные.",
    description:
      "Уведомление над картой, когда сервер обрезал выдачу лимитом (хоким, менеджер); {shown} и {matchingText} — готовые числа с разделителями, {count} — то же общее число для склонения",
  },
  percent: {
    id: "datamap.model.percent",
    defaultMessage: "{value} %",
    description: "Процент в легенде карты («12,5 %»); {value} — число, уже отформатированное",
  },
  themeMassif: {
    id: "datamap.thematic.massif",
    defaultMessage: "Массив",
    description: "Вариант переключателя «Раскраска контуров» на карте: красить по массиву; коротко",
  },
  themeMassifHint: {
    id: "datamap.thematic.massifHint",
    defaultMessage: "факт реестра",
    description:
      "Пояснение к варианту раскраски «Массив» в переключателе карты: данные взяты из реестра, не рассчитаны; строчная, коротко",
  },
  themeCrop: {
    id: "datamap.thematic.cropDemo",
    defaultMessage: "Культура (ДЕМО)",
    description:
      "Вариант раскраски контуров на карте: по культуре; ДЕМО — данные синтетические, слово оставить заглавными",
  },
  themeCropHint: {
    id: "datamap.thematic.cropDemoHint",
    defaultMessage: "синтетическая детализация размещения",
    description:
      "Пояснение к варианту «Культура (ДЕМО)»: культуры на контурах учебные, не из реестра; строчная",
  },
  themeQuarantine: {
    id: "datamap.thematic.quarantine",
    defaultMessage: "Карантинный статус",
    description:
      "Недоступный вариант раскраски карты: по карантинному статусу контура; коротко",
  },
  themeQuarantineHint: {
    id: "datamap.thematic.quarantineHint",
    defaultMessage: "в данных карты этого пока нет: у контура нет статуса",
    description:
      "Почему недоступна раскраска по карантинному статусу: источник данных карты его не отдаёт; строчная",
  },
  themeWork: {
    id: "datamap.thematic.workState",
    defaultMessage: "Состояние работы",
    description:
      "Недоступный вариант раскраски карты: по состоянию работы на контуре; коротко",
  },
  themeWorkHint: {
    id: "datamap.thematic.workStateHint",
    defaultMessage: "в данных карты этого пока нет: у контура нет состояния работы",
    description:
      "Почему недоступна раскраска по состоянию работы: источник данных карты его не отдаёт; строчная",
  },
  dropFrame: {
    id: "datamap.carry.frame",
    defaultMessage: "рамка кадра",
    description:
      "Название сужения выборки, которое не переехало с карты в список контуров (имя в перечне «не перенесено»); строчная, коротко",
  },
  dropFrameWhy: {
    id: "datamap.carry.frameWhy",
    defaultMessage: "список контуров рамку не принимает: он останется по всему району",
    description: "Причина, почему рамка кадра карты не переносится в список контуров; строчная, одно предложение без точки",
  },
  dropCrop: {
    id: "datamap.carry.crop",
    defaultMessage: "раскраска по культуре",
    description:
      "Название сужения выборки, не переехавшего с карты в список контуров; строчная, коротко",
  },
  dropCropWhy: {
    id: "datamap.carry.cropWhy",
    defaultMessage: "в списке контуров нет отбора по культуре — раскраска туда не переносится",
    description: "Причина, почему раскраска карты по культуре не переносится в список контуров; строчная, без точки",
  },
  dropFarm: {
    id: "datamap.carry.farm",
    defaultMessage: "выделенное хозяйство",
    description:
      "Название сужения выборки (хозяйство, подсвеченное на карте), не переехавшего в список контуров; строчная, коротко",
  },
  dropFarmWhy: {
    id: "datamap.carry.farmWhy",
    defaultMessage: "список контуров не умеет отбирать по землепользователю",
    description: "Причина, почему выделение хозяйства на карте не переносится в список контуров; строчная, без точки",
  },
  dropQuery: {
    id: "datamap.carry.query",
    defaultMessage: "поиск по номеру",
    description:
      "Название сужения выборки списка контуров, не переехавшего на карту; строчная, коротко",
  },
  dropQueryWhy: {
    id: "datamap.carry.queryWhy",
    defaultMessage: "карта не ищет по номеру: в кадре останутся все контуры",
    description: "Причина, почему поиск по номеру контура из списка не переносится на карту; строчная, без точки",
  },
  dropIssues: {
    id: "datamap.carry.issues",
    defaultMessage: "отбор по замечаниям",
    description:
      "Название сужения выборки списка контуров (только с замечаниями качества), не переехавшего на карту; строчная, коротко",
  },
  dropIssuesWhy: {
    id: "datamap.carry.issuesWhy",
    defaultMessage: "карта ничего не знает о замечаниях к данным",
    description: "Причина, почему отбор «с замечаниями» из списка контуров не переносится на карту; строчная, без точки",
  },
  dropNoGeometry: {
    id: "datamap.carry.noGeometry",
    defaultMessage: "«без принятой геометрии»",
    description:
      "Название отбора списка контуров (контуры без принятого полигона), не переехавшего на карту; в кавычках, как название отбора",
  },
  dropNoGeometryWhy: {
    id: "datamap.carry.noGeometryWhy",
    defaultMessage: "это противоположная выборка: контур без полигона на карте не рисуется вовсе",
    description: "Причина, почему отбор «без принятой геометрии» не переносится на карту; строчная, без точки",
  },
});

/** [minLon, minLat, maxLon, maxLat] — порядок GeoJSON и порядок API. */
export type Bbox = [number, number, number, number];

/**
 * Рамка района на случай, если `/map/reference` не ответил. Это не «примерная
 * геометрия»: рамка задаёт только положение камеры, ничего не рисуя.
 */
export const DISTRICT_FALLBACK_BBOX: Bbox = [68.962, 40.6019, 69.3647, 40.8688];

/* ------------------------------------------------------------------ */
/* Камера → параметры запроса                                          */
/* ------------------------------------------------------------------ */

/** Ровно та часть `LngLatBounds`, что нужна модели: карту в тест не тянем. */
export interface BoundsLike {
  getWest: () => number;
  getSouth: () => number;
  getEast: () => number;
  getNorth: () => number;
}

export interface MapView {
  bbox: string;
  zoom: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Видимая рамка → строка `minLon,minLat,maxLon,maxLat`.
 *
 * Координаты обрезаются по границам мира: на малом зуме рамка карты шире
 * планеты, и такой bbox сервер честно отвергает. Округление до 5 знаков
 * (≈1 м) заодно делает URL стабильным при дрожании камеры.
 */
export function boundsToBbox(bounds: BoundsLike, precision = 5): string {
  const west = clamp(bounds.getWest(), -180, 180);
  const east = clamp(bounds.getEast(), -180, 180);
  const south = clamp(bounds.getSouth(), -90, 90);
  const north = clamp(bounds.getNorth(), -90, 90);
  return [west, south, east, north]
    .map((value) => Number(value.toFixed(precision)))
    .join(",");
}

/** Зум для сервера: целый и в пределах шкалы. Нечисло — это зум 0, а не NaN. */
export function viewZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 0;
  return clamp(Math.round(zoom), 0, 22);
}

/* ------------------------------------------------------------------ */
/* Палитра массивов                                                    */
/* ------------------------------------------------------------------ */

/** Оттенок токена `--active`: палитра вырастает из основного цвета продукта. */
const HUE_ORIGIN = 152;
/** Золотой угол: соседние индексы расходятся по кругу максимально далеко. */
const HUE_STEP = 137.508;
/** Светлота и насыщенность идут своими циклами — чтобы близкие тона всё равно
    отличались по весу, а не только по оттенку (в т.ч. при дальтонизме). */
const LIGHTNESS = [38, 52, 64];
const SATURATION = [58, 42, 70];

function cycle(values: ReadonlyArray<number>, index: number): number {
  const position = ((index % values.length) + values.length) % values.length;
  return values[position] ?? 0;
}

/** Цвет массива — чистая функция индекса: один и тот же вход даёт тот же цвет. */
export function massifColor(index: number): string {
  const hue = (((HUE_ORIGIN + index * HUE_STEP) % 360) + 360) % 360;
  return `hsl(${hue.toFixed(1)}, ${cycle(SATURATION, index)}%, ${cycle(LIGHTNESS, index)}%)`;
}

export interface MassifPaletteEntry {
  id: number;
  name: string;
  color: string;
}

/**
 * Цвет привязан к месту массива в отсортированном по названию списке, а НЕ к
 * порядку ответа сервера: иначе перезапрос перекрашивал бы карту, и цвет
 * переставал бы что-либо значить.
 */
export function buildMassifPalette(
  massifs: ReadonlyArray<{ id: number; canonical_name: string }>,
): MassifPaletteEntry[] {
  return [...massifs]
    .sort(
      (left, right) =>
        left.canonical_name.localeCompare(right.canonical_name, "ru") ||
        left.id - right.id,
    )
    .map((massif, index) => ({
      id: massif.id,
      name: massif.canonical_name,
      color: massifColor(index),
    }));
}

/**
 * Выражение maplibre «massif_id → цвет». Массив, которого нет в палитре,
 * красится запасным цветом и остаётся видимым — молча пропасть он не должен.
 */
export function massifColorExpression(
  palette: ReadonlyArray<MassifPaletteEntry>,
  fallback: string,
): DataDrivenPropertyValueSpecification<ColorSpecification> {
  // `match` без единой пары невалиден — пустая палитра это просто один цвет.
  if (palette.length === 0) return fallback;
  const expression: unknown[] = ["match", ["get", "massif_id"]];
  for (const entry of palette) {
    expression.push(entry.id, entry.color);
  }
  expression.push(fallback);
  // Единственное приведение в модуле: форма выражения собрана циклом, и статикой
  // кортежный тип style-spec из неё не выводится.
  return expression as unknown as DataDrivenPropertyValueSpecification<ColorSpecification>;
}

/* ------------------------------------------------------------------ */
/* Палитра культур — ДЕМО-слой (каскад L2)                             */
/* ------------------------------------------------------------------ */

/**
 * Мозаика культур — ТОКЕНАМИ, а не вычисленным оттенком (ADR-0009).
 *
 * Раньше цвет культуры считался золотым углом от произвольного начала. Это
 * давало разные цвета, но не давало НИ ОДНОЙ гарантии: соседние культуры могли
 * оказаться неразличимы при дальтонизме, а «прочие» — ярче хлопка. Теперь
 * ступени взяты из проверенной палитры данных, порядок их назначения совпадает
 * с `CROP_ORDER`, и порядок этот фиксирован: культура не перекрашивается от
 * того, что попало в кадр.
 *
 * Ступени проверены валидатором палитр в режиме «ВСЕ ПАРЫ» — том, который и
 * описывает карту: ведущие культуры кадра выбираются по числу контуров, значит
 * рядом может оказаться любая пара из семи, а не соседняя по списку. Проверка
 * «первых четырёх» этого не доказывала: четвёрка в кадре — любая из
 * тридцати пяти. Теперь порог держат ВСЕ 21 пара семи опознавательных ступеней
 * плюс нейтраль «прочие» против каждой из них, в обеих темах; перебор считает
 * `styles/cropPalette.test.ts`, числа — в ADR-0009.
 */
const CROP_COLOR_TOKENS: Readonly<Record<string, string>> = {
  COTTON: "--data-crop-cotton",
  GRAIN: "--data-crop-grain",
  POTATO: "--data-crop-potato",
  RICE: "--data-crop-rice",
  FODDER: "--data-crop-fodder",
  MELON: "--data-crop-melon",
  LEGUMES: "--data-crop-legumes",
  OTHER_CROPS: "--data-crop-other",
};

/** Культура вне словаря — не новая личность: она уходит в «размещение неизвестно». */
const CROP_UNKNOWN_TOKEN = "--data-crop-unknown";

/**
 * Сколько культур карта красит одновременно.
 *
 * Это потолок ПЛОТНОСТИ ЛЕГЕНДЫ, а не цвета: различимость держат сами ступени —
 * порог проходит каждая пара из семи, поэтому безопасна любая четвёрка, любая
 * пятёрка и весь набор. Раньше здесь стояло обратное утверждение («пятая
 * заливка ломает различимость»), и оно было неправдой в обе стороны: замер
 * покрывал только первые четыре ступени списка, а карта красит ведущие ЧЕТЫРЕ
 * КАДРА — то есть любую четвёрку.
 *
 * Четыре — это про чтение: легенда на семь строк рядом с картой перестаёт быть
 * легендой, а пятая-шестая культура в кадре района почти всегда единичные
 * контуры. Хвост не теряется: он сворачивается в «прочие», остаётся числом в
 * легенде и строкой в таблице-компаньоне.
 */
export const MAP_CROP_FILL_CAP = 4;

/**
 * Токен → готовый цвет. maplibre не понимает `var()`, поэтому значение
 * разворачивается настоящей пробой в документе — тем же способом, что и
 * остальные цвета полотна.
 *
 * Там, где `var()` не поддерживается вовсе (тестовая среда без CSS), проба
 * честно отказывается и возвращает сам `var(--токен)`: это остаётся
 * детерминированным и различимым значением, но НИ ПРИ КАКИХ УСЛОВИЯХ не
 * превращается в сырой hex, вписанный в код.
 */
export function cropTokenColor(token: string): string {
  if (typeof document === "undefined" || !document.body) return `var(${token})`;
  const probe = document.createElement("span");
  probe.style.display = "none";
  document.body.appendChild(probe);
  // Проба поддержки: подстановка с запасным значением обязана дать ровно его.
  probe.style.color = "var(--agroai-probe-absent, rgb(1, 2, 3))";
  const supported = getComputedStyle(probe).color === "rgb(1, 2, 3)";
  let value = `var(${token})`;
  if (supported) {
    probe.style.color = `var(${token})`;
    const resolved = getComputedStyle(probe).color;
    if (resolved) value = resolved;
  }
  probe.remove();
  return value;
}

export interface CropPaletteEntry {
  code: string;
  name: string;
  color: string;
  /** Токен ступени: цвет всегда можно проверить против дизайн-системы. */
  token: string;
}

/**
 * Палитра культур: тот же вход — тот же цвет, независимо от данных в кадре.
 * Резолвер передаётся снаружи там, где нужен свой (тест, снимок, печать).
 */
export function buildCropPalette(
  resolve: (token: string) => string = cropTokenColor,
): CropPaletteEntry[] {
  return CROP_ORDER.map((code) => {
    const token = CROP_COLOR_TOKENS[code] ?? CROP_UNKNOWN_TOKEN;
    return { code, name: cropLabel(code).label, color: resolve(token), token };
  });
}

/**
 * Сворачивает хвост палитры до `cap` ведущих культур КАДРА.
 *
 * Ведущие выбираются по числу контуров в кадре, а порядок внутри выборки
 * остаётся порядком `CROP_ORDER`: культура не меняет цвет от того, что стала
 * второй вместо третьей. Свёрнутое не исчезает — оно возвращается отдельной
 * записью «прочие» с перечисленными кодами, чтобы легенда назвала его вслух.
 */
export function foldCropPalette(
  palette: ReadonlyArray<CropPaletteEntry>,
  byCrop: ReadonlyMap<string, number>,
  cap: number = MAP_CROP_FILL_CAP,
): { shown: CropPaletteEntry[]; folded: string[] } {
  const present = palette.filter((entry) => (byCrop.get(entry.code) ?? 0) > 0);
  if (present.length <= cap) return { shown: [...present], folded: [] };
  const leading = new Set(
    [...present]
      .sort(
        (left, right) =>
          (byCrop.get(right.code) ?? 0) - (byCrop.get(left.code) ?? 0) ||
          palette.indexOf(left) - palette.indexOf(right),
      )
      .slice(0, cap)
      .map((entry) => entry.code),
  );
  return {
    shown: present.filter((entry) => leading.has(entry.code)),
    folded: present.filter((entry) => !leading.has(entry.code)).map((e) => e.code),
  };
}

/**
 * Выражение maplibre «crop_demo → цвет».
 *
 * `crop_demo` — это культура, стоящая на контуре НА ДАТУ СРЕЗА, а не «за
 * сезон»: гектар за год несёт хлопок, потом пшеницу, потом второстепенную, и
 * один цвет на весь год был бы неправдой в большей части кадра.
 *
 * Контур без занятости на эту дату (`crop_demo === null`) и контур с
 * незнакомым кодом приходят к одному запасному цвету намеренно: и то и другое
 * означает «этот полигон тематика не описывает», и выдавать их за категорию
 * нельзя. Пустой январь — законный ответ, а не пробел в данных.
 */
export function cropColorExpression(
  palette: ReadonlyArray<CropPaletteEntry>,
  fallback: string,
): DataDrivenPropertyValueSpecification<ColorSpecification> {
  if (palette.length === 0) return fallback;
  const expression: unknown[] = ["match", ["to-string", ["get", "crop_demo"]]];
  for (const entry of palette) {
    expression.push(entry.code, entry.color);
  }
  expression.push(fallback);
  return expression as unknown as DataDrivenPropertyValueSpecification<ColorSpecification>;
}

/** Тематика заливки контуров: по массивам (факт) или по культурам (демо). */
export type Thematic = "massif" | "crop_demo";

/**
 * Сводка демо-слоя в кадре: сколько контуров какая культура занимает СЕЙЧАС.
 *
 * Считается по ТОМУ ЖЕ ответу, который раскрашен, а не отдельным запросом:
 * легенда и карта обязаны говорить об одном и том же кадре и об одной дате.
 */
export function cropCountsInView(
  features: ReadonlyArray<ContourFeature>,
): { byCrop: Map<string, number>; painted: number; unpainted: number } {
  const byCrop = new Map<string, number>();
  let painted = 0;
  let unpainted = 0;
  for (const feature of features) {
    const code = feature.properties.crop_demo;
    if (!code) {
      unpainted += 1;
      continue;
    }
    painted += 1;
    byCrop.set(code, (byCrop.get(code) ?? 0) + 1);
  }
  return { byCrop, painted, unpainted };
}

/* ------------------------------------------------------------------ */
/* Словари слоя геометрии                                              */
/* ------------------------------------------------------------------ */

/**
 * Ярус принятой связи «фича NGIS ↔ контур». Правило то же, что в
 * `lib/epistemic`: неизвестный код показывается как неизвестный, а не
 * подменяется похожим знакомым.
 *
 * Словари не перечисляются здесь: они ВЫВЕДЕНЫ из `tierRegistry`. Пока они
 * были отдельными литералами, ярусы `merge` и `mn_group` приезжали с сервера в
 * каждой выдаче и подписывались как «неизвестно», а их отношения — как
 * отношение соседнего яруса.
 */
function lookup(
  dictionary: Record<string, string>,
  code: string | null | undefined,
): { label: string; known: boolean } {
  if (!code) return { label: tr(m.unknown), known: false };
  const label = dictionary[code];
  return label ? { label, known: true } : { label: tr(m.unknownCode, { code }), known: false };
}

export const tierLabel = (code: string | null | undefined) =>
  lookup(tierLabels(), code);
export const relationLabel = (code: string | null | undefined) =>
  lookup(relationLabels(), code);

/** Показывает ли эта геометрия несколько контуров сразу. */
export const isSharedRelation = (code: string | null | undefined): boolean =>
  !!code && MULTI_CONTOUR_RELATIONS.includes(code);

/** Незнакомый ярус уходит в конец списка, но не исчезает из него. */
export function tierRank(code: string): number {
  const index = TIER_ORDER.indexOf(code);
  return index === -1 ? TIER_ORDER.length : index;
}

/* ------------------------------------------------------------------ */
/* Подсказка                                                           */
/* ------------------------------------------------------------------ */

export interface ContourTip {
  contourNo: string;
  massif: string;
  massifMissing: boolean;
  landUser: string;
  landUserMissing: boolean;
  area: string;
  areaMissing: boolean;
  tier: string;
  tierKnown: boolean;
  relation: string;
  pieces: number;
  /**
   * Скольким контурам принадлежит ПОКАЗАННЫЙ полигон. 1 — он этого контура и
   * ничей больше. Больше — тот же полигон виден у соседей, и подсказка обязана
   * это сказать: иначе одинаковые полигоны читаются как ошибка данных.
   */
  sharedContours: number;
  /** Готовая формулировка про общую геометрию; `null` — делить не с кем. */
  shared: string | null;
  /** ДЕМО. `null` — детализации нет либо демо-слой не запрошен. */
  cropDemo: {
    label: string;
    known: boolean;
    area: string;
    crops: number;
    /** Роль культуры в цикле: «основная» / «второстепенная». */
    role: string;
    /** Фаза цикла: «Хлопок», «Озимые зерновые», «Второстепенная культура». */
    phase: string;
  } | null;
}

/**
 * Текст подсказки под курсором.
 *
 * Название массива в фиче не приходит — только `massif_id`, поэтому имя
 * подставляется из справочника. Пока справочник не загружен, показывается
 * честный `массив #id`, а не пустота: идентификатор — настоящее значение.
 */
export function contourTip(
  props: ContourFeatureProps,
  massifName: string | null | undefined,
): ContourTip {
  const many = props.land_users_count > 1;
  const area = formatArea(props.total_microha);
  const tier = tierLabel(props.tier);
  const crop = cropLabel(props.crop_demo);
  const cropArea = formatArea(props.crop_demo_microha);
  const cropRole = cropRoleLabel(props.crop_demo_role);
  const cropPhase = rotationPhaseLabel(props.crop_demo_phase);

  return {
    // Демо появляется в подсказке ТОЛЬКО когда сервер прислал культуру, то
    // есть только при include_demo=true. Отдельной проверки флага здесь нет
    // намеренно: одна ветка вместо двух не разъедется с сервером.
    cropDemo: props.crop_demo
      ? {
          label: crop.label,
          known: crop.known,
          area: cropArea.missing ? missingLabel() : tr(m.haValue, { value: cropArea.text }),
          crops: props.crop_demo_crops,
          role: cropRole.label,
          phase: cropPhase.label,
        }
      : null,
    contourNo: props.contour_no,
    massif: massifName ?? tr(m.massifUnnamed, { id: props.massif_id }),
    massifMissing: !massifName,
    // «Несколько» — не имя и не пустота: у нарезанного контура нет «главного»
    // землепользователя, и выбирать его за реестр клиент не имеет права.
    landUser: many
      ? tr(m.landUserMany, { count: props.land_users_count })
      : (props.land_user ?? missingLabel()),
    landUserMissing: !many && !props.land_user,
    area: area.missing ? missingLabel() : tr(m.haValue, { value: area.text }),
    areaMissing: area.missing,
    tier: tier.label,
    tierKnown: tier.known,
    relation: relationLabel(props.relation).label,
    pieces: props.pieces,
    sharedContours: props.shared_contours,
    // Формулировка одна и та же в подсказке и в легенде: пользователь видит
    // один полигон у нескольких контуров и должен прочесть это как отношение.
    shared:
      props.shared_contours > 1
        ? `${sharedGeometryNote()} (${props.shared_contours})`
        : null,
  };
}

/* ------------------------------------------------------------------ */
/* Усечение выдачи                                                     */
/* ------------------------------------------------------------------ */

export interface TruncationNotice {
  shown: number;
  matching: number;
  hidden: number;
  text: string;
}

/**
 * «Показано X из Y». Молчаливого усечения быть не должно: если сервер обрезал
 * выдачу лимитом, пользователь узнаёт об этом от нас, а не по недостающим
 * полигонам.
 */
export function truncationNotice(response: {
  truncated: boolean;
  returned: number;
  matching: number;
}): TruncationNotice | null {
  if (!response.truncated) return null;
  const shown = formatCount(response.returned).text;
  const matching = formatCount(response.matching).text;
  return {
    shown: response.returned,
    matching: response.matching,
    hidden: Math.max(0, response.matching - response.returned),
    text: tr(m.truncation, {
      shown,
      matchingText: matching,
      count: response.matching,
    }),
  };
}

/* ------------------------------------------------------------------ */
/* Отбор геометрии для слоёв                                           */
/* ------------------------------------------------------------------ */

/** Рисуем только площадные типы: точки и линии контуром района не бывают. */
const DRAWABLE_TYPES = new Set(["Polygon", "MultiPolygon"]);

/**
 * Есть ли у фичи ПРИНЯТАЯ геометрия.
 *
 * Проверка положительная и полная: `null`, пустой объект, geometry без
 * координат и geometry с пустыми координатами — всё это «геометрии нет».
 * Ничего из этого на карту не попадает ни в каком виде.
 */
export function hasAcceptedGeometry(feature: {
  geometry: { [key: string]: unknown } | null;
}): boolean {
  const geometry = feature.geometry;
  if (!geometry || typeof geometry !== "object") return false;
  if (typeof geometry.type !== "string" || !DRAWABLE_TYPES.has(geometry.type)) {
    return false;
  }
  return Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0;
}

/** Минимальная форма GeoJSON, которую понимает источник maplibre. */
export interface LayerFeature<P> {
  type: "Feature";
  id: number;
  geometry: { [key: string]: unknown };
  properties: P;
}

export interface LayerData<P> {
  type: "FeatureCollection";
  features: LayerFeature<P>[];
}

function toLayerData<P>(
  features: ReadonlyArray<{
    id: number;
    geometry: { [key: string]: unknown } | null;
    properties: P;
  }>,
): LayerData<P> {
  return {
    type: "FeatureCollection",
    features: features.filter(hasAcceptedGeometry).map((feature) => ({
      type: "Feature",
      id: feature.id,
      // Проверено `hasAcceptedGeometry` строкой выше.
      geometry: feature.geometry as { [key: string]: unknown },
      properties: feature.properties,
    })),
  };
}

/**
 * ЕДИНСТВЕННЫЙ вход контуров на карту.
 *
 * Контур без принятой геометрии не подсвечивается никак — он живёт в списке
 * «без геометрии», а список это не слой. Выдуманный центроид визуально
 * неотличим от настоящего полигона и мгновенно становится ложным
 * доказательством, поэтому «примерных» отметок здесь нет и быть не может.
 */
export function contourLayerData(
  features: ReadonlyArray<ContourFeature>,
): LayerData<ContourFeatureProps> {
  return toLayerData(features);
}

export type ReferenceProps = ReferenceFeature["properties"];

/** Контекстные слои (район, махалли) — тот же фильтр, то же правило. */
export function referenceLayerData(
  features: ReadonlyArray<ReferenceFeature>,
): LayerData<ReferenceProps> {
  return toLayerData(features);
}

/* ------------------------------------------------------------------ */
/* Рамка по геометрии                                                  */
/* ------------------------------------------------------------------ */

function extend(box: Bbox, node: unknown): void {
  if (!Array.isArray(node)) return;
  const [lon, lat] = node;
  if (typeof lon === "number" && typeof lat === "number") {
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    box[0] = Math.min(box[0], lon);
    box[1] = Math.min(box[1], lat);
    box[2] = Math.max(box[2], lon);
    box[3] = Math.max(box[3], lat);
    return;
  }
  for (const child of node) extend(box, child);
}

/**
 * Рамка по координатам фич — для первого кадра камеры. Считает только
 * положение камеры и ничего не утверждает о площадях.
 */
export function geoBounds(
  features: ReadonlyArray<{ geometry: { [key: string]: unknown } | null }>,
): Bbox | null {
  const box: Bbox = [
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
  ];
  for (const feature of features) {
    if (!hasAcceptedGeometry(feature)) continue;
    extend(box, feature.geometry?.coordinates);
  }
  return box.every((value) => Number.isFinite(value)) ? box : null;
}

/* ------------------------------------------------------------------ */
/* Доли покрытия                                                       */
/* ------------------------------------------------------------------ */

const PERCENT = new Intl.NumberFormat("ru-RU", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Доля в процентах. Нулевой знаменатель — это «доли нет», а не «0 %». */
export function share(part: number, total: number): number | null {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return null;
  return (part / total) * 100;
}

export function formatShare(value: number | null): string {
  return value === null ? missingLabel() : tr(m.percent, { value: PERCENT.format(value) });
}

/* ------------------------------------------------------------------ */
/* Карта как ВИД УРОВНЯ: тематика, кадр, общий словарь фильтров        */
/* ------------------------------------------------------------------ */

/**
 * Тематики заливки — перечислены реестром, а не тернарником в разметке.
 *
 * Недоступные тематики стоят в списке и НЕ выбираются. Это не обещание на
 * будущее: у карантинного статуса и состояния работы на карте нет источника —
 * `/map/contours` не отдаёт по фиче ни того, ни другого. Спрятать их значило бы
 * оставить читателя гадать, почему «раскрасить по карантину» нигде нет; сказать
 * прямо — назвать границу версии там, где её ищут.
 */
export interface ThematicOption {
  value: string;
  label: string;
  /** Чем этот вариант отличается от соседнего — и почему он недоступен. */
  hint: string;
  available: boolean;
}

export const THEMATIC_OPTIONS: readonly ThematicOption[] = [
  {
    value: "massif",
    get label() {
      return tr(m.themeMassif);
    },
    get hint() {
      return tr(m.themeMassifHint);
    },
    available: true,
  },
  {
    value: "crop_demo",
    get label() {
      return tr(m.themeCrop);
    },
    get hint() {
      return tr(m.themeCropHint);
    },
    available: true,
  },
  {
    value: "quarantine",
    get label() {
      return tr(m.themeQuarantine);
    },
    get hint() {
      return tr(m.themeQuarantineHint);
    },
    available: false,
  },
  {
    value: "work_state",
    get label() {
      return tr(m.themeWork);
    },
    get hint() {
      return tr(m.themeWorkHint);
    },
    available: false,
  },
];

/**
 * Тематика из адреса. Недоступная и незнакомая — это «массив»: раскрашивать
 * нечем, а показать пустую карту под чужой подписью хуже, чем показать факт.
 */
export function parseThematic(raw: string | null | undefined): Thematic {
  const found = THEMATIC_OPTIONS.find(
    (option) => option.value === raw && option.available,
  );
  return found?.value === "crop_demo" ? "crop_demo" : "massif";
}

/**
 * Массивы, реально попавшие в кадр, — вход всегда видимой легенды.
 *
 * Легенда обещает ровно то, что видно: список всех массивов района под
 * свёрнутым `<details>` не отвечал на вопрос «а это что за цвет под курсором».
 */
export function massifCountsInView(
  features: ReadonlyArray<ContourFeature>,
): { byMassif: Map<number, number>; total: number } {
  const byMassif = new Map<number, number>();
  for (const feature of features) {
    const id = feature.properties.massif_id;
    byMassif.set(id, (byMassif.get(id) ?? 0) + 1);
  }
  return { byMassif, total: features.length };
}

/**
 * Строки таблицы-компаньона: то же, что на полотне, но достижимое клавиатурой.
 *
 * Порядок — «естественный числовой», тот же, что в реестре контуров: иначе 131
 * оказывается между 13082 и 13114, и один и тот же список читается на двух
 * экранах по-разному.
 */
export function frameRows(
  features: ReadonlyArray<ContourFeature>,
): ContourFeatureProps[] {
  return features
    .map((feature) => feature.properties)
    .sort((left, right) => {
      const byLength = left.contour_no.length - right.contour_no.length;
      if (byLength !== 0) return byLength;
      const byNo = left.contour_no.localeCompare(right.contour_no, "ru");
      return byNo !== 0 ? byNo : left.contour_id - right.contour_id;
    });
}

/* ------------------------------------------------------------------ */
/* DC-06: общий словарь фильтров «то же списком» / «то же на карте»    */
/* ------------------------------------------------------------------ */

/** Что сейчас сужает выборку карты. */
export interface MapFilterState {
  massifId: number | null;
  thematic: Thematic;
  /** Фокус на хозяйстве: клиентская подсветка, а не серверный фильтр. */
  actorId: number | null;
  /** Рамка кадра — она у карты есть всегда, а у реестра её принять нечем. */
  bbox: string | null;
}

export interface CarriedFilters {
  /** То, что реестр контуров принимает и понимает так же, как карта. */
  carried: Record<string, string | number>;
  /** То, что на список НЕ переехало, — с причиной. */
  dropped: { label: string; why: string }[];
}

/**
 * Перевод фильтров карты в фильтры реестра контуров.
 *
 * Ключевое здесь — второй список. Переход «то же списком», который молча теряет
 * половину сужения, показывает ДРУГУЮ выборку под тем же словом «то же»: с
 * карты массива №7 в кадре одного квартала человек попадает в реестр всего
 * района и считает, что видит те же контуры. Поэтому потерянное называется, а
 * не выбрасывается.
 *
 * `has_geometry=true` добавляется всегда: на карте по определению только
 * контуры с ПРИНЯТОЙ геометрией, и список без этого фильтра был бы шире карты.
 */
export function contourListFilters(state: MapFilterState): CarriedFilters {
  const carried: Record<string, string | number> = { has_geometry: "true" };
  const dropped: CarriedFilters["dropped"] = [];

  if (state.massifId !== null) carried.massif_id = state.massifId;

  if (state.bbox !== null) {
    dropped.push({
      label: tr(m.dropFrame),
      why: tr(m.dropFrameWhy),
    });
  }
  if (state.thematic === "crop_demo") {
    dropped.push({
      label: tr(m.dropCrop),
      why: tr(m.dropCropWhy),
    });
  }
  if (state.actorId !== null) {
    dropped.push({
      label: tr(m.dropFarm),
      why: tr(m.dropFarmWhy),
    });
  }

  return { carried, dropped };
}

/** Что сейчас сужает выборку реестра контуров. */
export interface ContourListFilterState {
  massifId: string | null;
  /** Поиск по номеру контура. */
  query: string;
  /** "true" | "false" | null — как это лежит в адресе реестра. */
  hasIssues: string | null;
  hasGeometry: string | null;
}

/**
 * Обратный перевод: фильтры реестра контуров → фильтры карты.
 *
 * Живёт рядом с `contourListFilters` намеренно. Два перехода между одними и
 * теми же двумя видами — это одно правило, прочитанное с разных сторон; в
 * двух файлах оно однажды разойдётся, и «то же на карте» начнёт показывать не
 * то, что «то же списком».
 *
 * Карта принимает от реестра ТОЛЬКО массив. Отдельно назван случай
 * `has_geometry=false`: это не потерянное сужение, а прямо противоположная
 * выборка — контуры без полигона на карте не существуют, и переход туда
 * показал бы пустое полотно, если промолчать.
 */
export function mapFiltersFromContourList(
  state: ContourListFilterState,
): CarriedFilters {
  const carried: Record<string, string | number> = {};
  const dropped: CarriedFilters["dropped"] = [];

  if (state.massifId !== null && state.massifId !== "") {
    carried.massif_id = state.massifId;
  }

  if (state.query !== "") {
    dropped.push({
      label: tr(m.dropQuery),
      why: tr(m.dropQueryWhy),
    });
  }
  if (state.hasIssues !== null && state.hasIssues !== "") {
    dropped.push({
      label: tr(m.dropIssues),
      why: tr(m.dropIssuesWhy),
    });
  }
  if (state.hasGeometry === "false") {
    dropped.push({
      label: tr(m.dropNoGeometry),
      why: tr(m.dropNoGeometryWhy),
    });
  }

  return { carried, dropped };
}
